import React, { useCallback, useMemo } from 'react';
import { YearViewProps, IEvent, CategoryColor } from '../core/type';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { getEventCategoryColor, getEventEndDateTime } from '../utils';
import { RootState } from '../../../../../redux/store';
import { useSelector } from 'react-redux';
import { getEventDates, normalizeDate } from '../core/_utility';
import { CustomDateFormate, getSaTodayDate } from '../../../../../utils/date';

const YearView: React.FC<YearViewProps> = ({ currentDate, events, onMonthSelect }) => {
    const year = currentDate.getFullYear();
    const { categoryAndColorsData }: {
        categoryAndColorsData: CategoryColor[]
    } = useSelector((state: RootState) => state.eventReducer);

    const months = useMemo(() =>
        Array.from({ length: 12 }, (_, i) => new Date(year, i, 1)),
        [year]
    );

    const isMultiDayEvent = useCallback((event: IEvent): boolean => {
        const { start, end } = getEventDates(event);

        if (start && end) {
            const startDate = normalizeDate(start);
            const endDate = normalizeDate(end);

            return startDate.getTime() !== endDate.getTime();
        }

        // Fallback for backward compatibility
        const startDate = new Date(event.start_date);
        const endDate = new Date(event.end_date);
        return startDate.toDateString() !== endDate.toDateString();
    }, [getEventDates, normalizeDate]);

    // Get events for a specific date (including multi-day events that span this date)
    const getEventsForDateRange = useCallback((date: Date): IEvent[] => {
        const currentDateObj = normalizeDate(date);

        return events.filter(event => {
            let eventStart, eventEnd;
            const { start, end } = getEventDates(event);

            if (start && end) {
                eventStart = start;
                eventEnd = getEventEndDateTime(event);
            } else {
                // Fallback for backward compatibility
                eventStart = normalizeDate(event.start_date);
                eventEnd = normalizeDate(event.end_date);
            }

            // Check if current date falls within event range (inclusive)
            return currentDateObj >= eventStart && currentDateObj <= eventEnd;
        });
    }, [events, getEventEndDateTime, getEventDates, normalizeDate]);

    const getDaysInMonthGrid = useCallback((date: Date): Date[] => {
        const first = new Date(date.getFullYear(), date.getMonth(), 1);
        const start = new Date(first);
        start.setDate(first.getDate() - first.getDay());
        const days: Date[] = [];
        for (let i = 0; i < 35; i++) {
            const d = new Date(start);
            d.setDate(start.getDate() + i);
            days.push(d);
        }
        return days;
    }, []);

    const getDayEventInfo = useCallback(
        (dayEvents: IEvent[]) => {
            const eventCount = dayEvents.length;
            const multiDayCount = dayEvents.filter(isMultiDayEvent).length;
            const singleDayCount = eventCount - multiDayCount;

            const colors = [
                ...new Set(
                    dayEvents.map(event =>
                        getEventCategoryColor(event.category, categoryAndColorsData)
                    )
                ),
            ].slice(0, 3);

            return {
                hasEvent: eventCount > 0,
                eventCount,
                multiDayCount,
                singleDayCount,
                colors,
                events: dayEvents,
            };
        },
        [isMultiDayEvent, categoryAndColorsData]
    );

    const getEventPositionInMonth = useCallback((event: IEvent, monthDate: Date): 'start' | 'middle' | 'end' | 'single' => {
        let eventStart, eventEnd;
        const { start, end } = getEventDates(event);

        if (start && end) {
            eventStart = start;
            eventEnd = end;
        } else {
            // Fallback for backward compatibility
            eventStart = normalizeDate(event.start_date);
            eventEnd = normalizeDate(event.end_date);
        }

        const monthStart = new Date(year, monthDate.getMonth(), 1);
        const monthEnd = new Date(year, monthDate.getMonth() + 1, 0);

        monthStart.setHours(0, 0, 0, 0);
        monthEnd.setHours(0, 0, 0, 0);

        const isSingleDay = eventStart.getTime() === eventEnd.getTime();
        if (isSingleDay) return 'single';

        const startsInMonth = eventStart >= monthStart && eventStart <= monthEnd;
        const endsInMonth = eventEnd >= monthStart && eventEnd <= monthEnd;

        if (startsInMonth && endsInMonth) return 'single';
        if (startsInMonth && !endsInMonth) return 'start';
        if (!startsInMonth && endsInMonth) return 'end';
        return 'middle';
    }, [year, getEventDates, normalizeDate]);

    const isToday = useCallback((date: Date): boolean => {
        return date.toDateString() === getSaTodayDate().toDateString();
    }, []);

    const isCurrentMonth = useCallback((monthDate: Date): boolean => {
        const today = getSaTodayDate();
        return today.getFullYear() === monthDate.getFullYear() && today.getMonth() === monthDate.getMonth();
    }, []);

    const weekDays = useMemo(() => ['S', 'M', 'T', 'W', 'T', 'F', 'S'], []);

    const renderDayTooltip = useCallback((day: Date, eventInfo: ReturnType<typeof getDayEventInfo>) => {
        const dayEvents = eventInfo.events;

        return (
            <Tooltip id={`tooltip-${day.toISOString()}`} className="custom-tooltip">
                <div className="bg-white py-2 px-3 rounded-3 shadow-sm border border-gray-200" style={{ maxWidth: '300px' }}>
                    <div className="fw-semibold fs-7 mb-2 text-gray-700 border-bottom pb-1">
                        {CustomDateFormate(day)}
                    </div>
                    {dayEvents.length === 0 ? (
                        <div className="fs-9 text-gray-500">No events</div>
                    ) : (
                        <>
                            <div className="mt-2 pt-1 border-top">
                                {dayEvents.slice(0, 5).map(event => {
                                    const position = getEventPositionInMonth(event, new Date(year, day.getMonth(), 1));
                                    let positionText = '';
                                    if (position === 'start') positionText = ' (Starts)';
                                    else if (position === 'end') positionText = ' (Ends)';
                                    else if (position === 'middle') positionText = ' (Continues)';

                                    return (
                                        <div key={event.id} className="fs-9 mb-1 d-flex align-items-center gap-2">
                                            <div
                                                style={{
                                                    width: '8px',
                                                    height: '8px',
                                                    borderRadius: '2px',
                                                    backgroundColor: `${getEventCategoryColor(event.category, categoryAndColorsData)}20`,
                                                    border: `1px solid ${getEventCategoryColor(event.category, categoryAndColorsData)}`
                                                }}
                                            />
                                            <span className="text-gray-700">
                                                {event.title}
                                                {isMultiDayEvent(event) && (
                                                    <span className="text-muted" style={{ fontSize: '8px', marginLeft: '4px' }}>
                                                        {positionText}
                                                    </span>
                                                )}
                                            </span>
                                        </div>
                                    );
                                })}
                                {dayEvents.length > 5 && (
                                    <div className="fs-9 text-primary mt-1">+{dayEvents.length - 5} more</div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </Tooltip>
        );
    }, [getEventPositionInMonth, isMultiDayEvent, year, categoryAndColorsData]);

    // MOVED THIS UP - eventsByDate is now defined before it's used in the render
    const eventsByDate = useMemo(() => {
        const map = new Map<string, IEvent[]>();

        events.forEach(event => {
            const { start, end } = getEventDates(event);

            if (start && end) {
                const current = normalizeDate(start);
                const last = normalizeDate(end);

                while (current <= last) {
                    const key = current.toISOString().split("T")[0];

                    if (!map.has(key)) {
                        map.set(key, []);
                    }

                    map.get(key)!.push(event);
                    current.setDate(current.getDate() + 1);
                }
            } else {
                // Fallback for backward compatibility
                const startDate = normalizeDate(event.start_date);
                const endDate = normalizeDate(event.end_date);
                const current = new Date(startDate);
                const last = new Date(endDate);

                while (current <= last) {
                    const key = current.toISOString().split("T")[0];

                    if (!map.has(key)) {
                        map.set(key, []);
                    }

                    map.get(key)!.push(event);
                    current.setDate(current.getDate() + 1);
                }
            }
        });

        return map;
    }, [events, getEventDates, normalizeDate]);

    return (
        <div className="year-view" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px', padding: '20px' }}>
            {months.map((monthDate, idx) => {
                const daysGrid = getDaysInMonthGrid(monthDate);
                const isCurrentMonthFlag = isCurrentMonth(monthDate);
                const monthEvents = events.filter(event => {
                    let eventStart, eventEnd;
                    const { start, end } = getEventDates(event);

                    if (start && end) {
                        eventStart = normalizeDate(start);
                        eventEnd = normalizeDate(end);
                    } else {
                        // Fallback for backward compatibility
                        eventStart = normalizeDate(event.start_date);
                        eventEnd = normalizeDate(event.end_date);
                    }

                    const monthStart = new Date(year, monthDate.getMonth(), 1);
                    const monthEnd = new Date(year, monthDate.getMonth() + 1, 0);

                    monthStart.setHours(0, 0, 0, 0);
                    monthEnd.setHours(0, 0, 0, 0);
                    return eventStart <= monthEnd && eventEnd >= monthStart;
                });
                const hasEventsInMonth = monthEvents.length > 0;

                return (
                    <div
                        key={monthDate.toISOString()}
                        className="day-event-block year-month-card rounded-3 p-3 cursor-pointer border border-gray-200 hover-shadow transition"
                        style={{
                            backgroundColor: 'white',
                            transition: 'all 0.2s ease',
                            cursor: 'pointer'
                        }}
                        onClick={() => onMonthSelect(monthDate)}
                    >
                        <div className={`fs-6 mb-3 fw-semibold text-center ${isCurrentMonthFlag ? 'text-primary' : 'text-gray-700'}`}>
                            {monthDate.toLocaleString('default', { month: 'long' })}
                            {hasEventsInMonth && (
                                <span className="ms-2 badge bg-primary bg-opacity-10 text-primary" style={{ fontSize: '10px' }}>
                                    {monthEvents.length}
                                </span>
                            )}
                        </div>
                        <div className="mini-calendar" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
                            {weekDays.map((day, i) => (
                                <div key={` ${day}-${i}`} className="text-center fs-9 fw-semibold px-3 py-2 text-gray-500" style={{ fontSize: '10px' }}>
                                    {day}
                                </div>
                            ))}
                            {daysGrid.map((day, i) => {
                                const isCurrentMonthDay = day.getMonth() === monthDate.getMonth();
                                const key = day.toISOString().split("T")[0];
                                const dayEvents = eventsByDate.get(key) || [];
                                const eventInfo = getDayEventInfo(dayEvents);
                                const today = isToday(day);
                                const hasMultiDay = eventInfo.multiDayCount > 0;
                                const hasSingleDay = eventInfo.singleDayCount > 0;

                                // Get the primary event color if exists
                                const primaryColor = eventInfo.colors[0] || '#3b82f6';

                                return (
                                    <OverlayTrigger
                                        key={day.toISOString()}
                                        placement="top"
                                        delay={{ show: 250, hide: 400 }}
                                        overlay={renderDayTooltip(day, eventInfo)}
                                    >
                                        <div
                                            className={`mini-day mb-0 text-center position-relative d-flex align-items-center justify-content-center`}
                                        >
                                            <p className="rounded-circle cursor-pointer mb-0 d-flex align-items-center justify-content-center"
                                                style={{
                                                    fontSize: '11px',
                                                    aspectRatio: '1',
                                                    backgroundColor: today ? '#3b82f6' : (eventInfo.hasEvent ? `${primaryColor}30` : 'transparent'),
                                                    color: today ? 'white' : (isCurrentMonthDay ? '#374151' : '#9ca3af'),
                                                    fontWeight: eventInfo.hasEvent ? '600' : 'normal',
                                                    cursor: isCurrentMonthDay ? 'pointer' : 'default',
                                                    transition: 'all 0.2s ease',
                                                    border: eventInfo.hasEvent && !today ? `1px solid ${primaryColor}` : 'none'
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (isCurrentMonthDay) {
                                                        e.currentTarget.style.backgroundColor = today ? '#2563eb' : `${primaryColor}60`;
                                                        e.currentTarget.style.transform = 'scale(1.05)';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (isCurrentMonthDay) {
                                                        e.currentTarget.style.backgroundColor = today ? '#3b82f6' : (eventInfo.hasEvent ? `${primaryColor}30` : 'transparent');
                                                        e.currentTarget.style.transform = 'scale(1)';
                                                    }
                                                }}
                                            >{day.getDate()}</p>
                                            {/* Event indicators */}
                                            {eventInfo.hasEvent && !today && (
                                                <div className="position-absolute" style={{ bottom: '2px', left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: '2px' }}>
                                                    {hasMultiDay && (
                                                        <div style={{
                                                            width: '4px',
                                                            height: '4px',
                                                            borderRadius: '1px',
                                                            backgroundColor: '#d9f523'
                                                        }} />
                                                    )}
                                                    {hasSingleDay && (
                                                        <div style={{
                                                            width: '4px',
                                                            height: '4px',
                                                            borderRadius: '1px',
                                                            backgroundColor: primaryColor
                                                        }} />
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </OverlayTrigger>
                                );
                            })}
                        </div>
                        {/* Month summary */}
                        {hasEventsInMonth && (
                            <div className="mt-2 pt-2 border-top" style={{ fontSize: '10px', color: '#6b7280' }}>
                                {monthEvents.slice(0, 2).map(event => {
                                    const position = getEventPositionInMonth(event, monthDate);
                                    let positionIcon = '';
                                    if (position === 'start') positionIcon = '↗ ';
                                    else if (position === 'end') positionIcon = '↖ ';
                                    else if (position === 'middle') positionIcon = '⋯ ';

                                    return (
                                        <div key={event.id} className="text-truncate mb-1">
                                            <span
                                                style={{
                                                    display: 'inline-block',
                                                    width: '8px',
                                                    height: '8px',
                                                    borderRadius: '2px',
                                                    backgroundColor: getEventCategoryColor(event.category, categoryAndColorsData),
                                                    marginRight: '4px'
                                                }}
                                            />
                                            {positionIcon}{event.title}
                                        </div>
                                    );
                                })}
                                {monthEvents.length > 2 && (
                                    <div className="text-primary">+{monthEvents.length - 2} more</div>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default YearView;